import React, { useEffect, useRef, useState } from 'react';

function pad2(n) {
  return String(n).padStart(2, '0');
}

function parseDob(value) {
  if (!value || typeof value !== 'string') return { day: '', month: '', year: '' };
  const parts = value.split('-');
  if (parts.length !== 3) return { day: '', month: '', year: '' };
  const [year, month, day] = parts;
  return {
    day: day && day !== '00' ? day : '',
    month: month && month !== '00' ? month : '',
    year: year && year !== '0000' ? year : '',
  };
}

/**
 * DD / MM / YYYY date-of-birth inputs. Emits YYYY-MM-DD via onChange.
 * onChange receives either a string value, or a synthetic input event if name is set.
 */
export default function DobFields({
  value = '',
  onChange,
  onBlur,
  name = 'dob',
  disabled = false,
  min,
  max,
  title,
  className = '',
  inputClassName = '',
  'data-table': dataTable,
}) {
  const initial = parseDob(value);
  const [day, setDay] = useState(initial.day);
  const [month, setMonth] = useState(initial.month);
  const [year, setYear] = useState(initial.year);

  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);

  useEffect(() => {
    const next = parseDob(value);
    setDay(next.day);
    setMonth(next.month);
    setYear(next.year);
  }, [value]);

  const emit = (d, m, y, isBlur = false) => {
    const complete = d.length === 2 && m.length === 2 && y.length === 4;
    const cleared = !d && !m && !y;
    if (!complete && !cleared && !isBlur) return;

    const composed = complete ? `${y}-${m}-${d}` : '';
    const synthetic = {
      target: {
        name,
        value: composed,
        dataset: dataTable ? { table: dataTable } : {},
      },
    };

    if ((complete || cleared) && typeof onChange === 'function') {
      onChange(synthetic);
    }
    if (isBlur && typeof onBlur === 'function') {
      onBlur(synthetic);
    }
  };

  const handleDay = (e) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    let next = raw;
    if (raw.length === 2) {
      const n = Math.min(31, Math.max(1, parseInt(raw, 10) || 1));
      next = pad2(n);
    }
    setDay(next);
    emit(next, month, year);
    if (next.length === 2) monthRef.current?.focus();
  };

  const handleMonth = (e) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    let next = raw;
    if (raw.length === 2) {
      const n = Math.min(12, Math.max(1, parseInt(raw, 10) || 1));
      next = pad2(n);
    }
    setMonth(next);
    emit(day, next, year);
    if (next.length === 2) yearRef.current?.focus();
  };

  const handleYear = (e) => {
    const next = e.target.value.replace(/\D/g, '').slice(0, 4);
    setYear(next);
    emit(day, month, next);
  };

  const handleFieldBlur = () => {
    emit(day, month, year, true);
  };

  return (
    <div className={`dob-fields ${className}`.trim()} title={title}>
      <input
        ref={dayRef}
        type="text"
        inputMode="numeric"
        placeholder="DD"
        aria-label="Day"
        className={`dob-input dob-day ${inputClassName}`.trim()}
        value={day}
        onChange={handleDay}
        onBlur={handleFieldBlur}
        disabled={disabled}
        maxLength={2}
        autoComplete="bday-day"
      />
      <input
        ref={monthRef}
        type="text"
        inputMode="numeric"
        placeholder="MM"
        aria-label="Month"
        className={`dob-input dob-month ${inputClassName}`.trim()}
        value={month}
        onChange={handleMonth}
        onBlur={handleFieldBlur}
        disabled={disabled}
        maxLength={2}
        autoComplete="bday-month"
      />
      <input
        ref={yearRef}
        type="text"
        inputMode="numeric"
        placeholder="YYYY"
        aria-label="Year"
        className={`dob-input dob-year ${inputClassName}`.trim()}
        value={year}
        onChange={handleYear}
        onBlur={handleFieldBlur}
        disabled={disabled}
        maxLength={4}
        autoComplete="bday-year"
        min={min}
        max={max}
      />
      <style>{`
        .dob-fields {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          margin-top: 3px;
        }
        .dob-fields .dob-input {
          box-sizing: border-box !important;
          height: 42px !important;
          padding: 8px 10px !important;
          margin: 0 !important;
          border: 1px solid #ccc !important;
          border-radius: 15px !important;
          background: #fff !important;
          text-align: center !important;
          font-size: 15px !important;
          color: #333 !important;
          outline: none;
        }
        .dob-fields .dob-input::placeholder {
          color: #9ca3af;
          text-align: center;
        }
        .dob-fields .dob-input:focus {
          border-color: #e53d5c !important;
          box-shadow: 0 0 0 2px rgba(229, 61, 92, 0.12);
        }
        .dob-fields .dob-input:disabled {
          background: #f3f4f6 !important;
          cursor: not-allowed;
        }
        .dob-fields .dob-day,
        .dob-fields .dob-month {
          width: 64px !important;
          flex: 0 0 64px !important;
          max-width: 64px !important;
        }
        .dob-fields .dob-year {
          flex: 1 1 auto !important;
          width: 100% !important;
          min-width: 96px !important;
          max-width: 140px !important;
        }
      `}</style>
    </div>
  );
}
